import { UserRoundX } from '@/lib/icons';
import { ReportPageLayout } from '@/components/reports';
import { reportsService } from '@/services/reports-service';
import type { ColumnDef, FilterConfig, ReportFilters } from '@/types/reports';
import { formatDisplayDateTime } from '@/lib/date-utils';

interface PortalActivity {
  id: string;
  attributes: {
    user_info: {
      first_name: string;
      last_name: string;
      email: string;
    };
    product_name?: string;
    activity: string;
    activity_date: string;
    affiliate_name?: string;
  };
}

const columns: ColumnDef<PortalActivity>[] = [
  {
    key: 'user',
    header: 'User',
    render: (item) => (
      <div>
        <div className="font-medium">
          {item.attributes.user_info.first_name} {item.attributes.user_info.last_name}
        </div>
        <div className="text-muted-foreground text-xs">
          {item.attributes.user_info.email}
        </div>
        {item.attributes.affiliate_name && (
          <div className="text-muted-foreground text-xs mt-0.5">
            {item.attributes.affiliate_name}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'product_name',
    header: 'Tool Name',
    render: (item) => item.attributes.product_name || '-',
  },
  {
    key: 'activity',
    header: 'Activity',
    render: (item) => item.attributes.activity,
  },
  {
    key: 'activity_date',
    header: 'Date',
    render: (item) => formatDisplayDateTime(item.attributes.activity_date),
  },
];

const filterConfig: FilterConfig = {
  fields: [
    { key: 'email_domain', type: 'text', label: 'Email Domain', placeholder: 'e.g., prosci.com' },
    { key: 'from_date', type: 'date', label: 'From Date', placeholder: 'Select date' },
    { key: 'to_date', type: 'date', label: 'To Date', placeholder: 'Select date' },
    { key: 'affiliate_id', type: 'affiliate-select', label: 'Affiliate', placeholder: 'All Affiliates', showFor: ['super', 'admin', 'affiliate_admin', 'data'] },
    { key: 'license_admin_id', type: 'organization-select', label: 'License Holder', placeholder: 'All License Holders', showFor: ['super', 'admin', 'affiliate_admin', 'data'] },
  ],
  hasFiltersCheck: (filters: ReportFilters) =>
    Boolean(
      filters.email_domain ||
      filters.from_date ||
      filters.to_date ||
      filters.affiliate_id ||
      filters.license_admin_id
    ),
};

export function PortalActivityReport() {
  return (
    <ReportPageLayout<PortalActivity>
      title="Portal Activity Report"
      description="Track user activity and interactions across portal tools."
      breadcrumbLabel="Portal Activity"
      itemLabel="activities"
      queryKey="portal-activity"
      fetchData={reportsService.getPortalActivity}
      downloadData={reportsService.downloadPortalActivity}
      filterConfig={filterConfig}
      columns={columns}
      sortData={(data) =>
        [...data].sort(
          (a, b) =>
            new Date(b.attributes.activity_date).getTime() -
            new Date(a.attributes.activity_date).getTime()
        )
      }
      getRowKey={(item) => item.id}
      emptyIcon={<UserRoundX />}
      emptyTitle="No activities found"
      emptyDescription="Try adjusting your filters to find activities."
    />
  );
}
